/** The ledger. Grouped by company by default, in the same order as the WhatsApp list,
 *  so learning one interface teaches the other.
 *
 *  Optionally grouped by **person** instead. "What is Shyam carrying today" is a more
 *  frequent question at eleven people than "what is in Pending approval", and it was
 *  the one question this table could not answer without reading every row.
 *
 *  Optionally grouped by date first — for the lists that span several days, where a
 *  company heading alone leaves you unable to tell which day a row belongs to. Today
 *  does not use it: it is one date, and a date heading there would be noise. */
import { Fragment } from 'react'
import type { Task } from '@/api/client'
import { approvalProgress } from '@/components/PeoplePicker'
import { Caret, CollapseAll, groupKey, useCollapsed } from './Collapse'
import { Tick, TickAll, type Selection } from './Selection'
import { fmtDate, fmtDay, MiniBar, PriorityEdge, StatusChip, Tag } from './ui'

/** One calculation, used by both grouping levels — so a day's bar and the company
 *  bars inside it can never disagree about the same rows. */
function countsOf(rows: Task[]) {
  return {
    completed: rows.filter((r) => r.status === 'COMPLETED').length,
    pending_approval: rows.filter((r) => r.status === 'PENDING_APPROVAL').length,
    rework: rows.filter((r) => r.status === 'REWORK').length,
    open: rows.filter((r) => r.status === 'OPEN').length,
  }
}

export type GroupBy = 'company' | 'person'

/** Which headings a task belongs under. One for a company; possibly several for a
 *  person, because a task two people share is on both of their plates.
 *
 *  `responsible_person` is deliberately NOT used for the person grouping: for a shared
 *  task it holds the joined string, so keying on it would invent an "Aarti, Shyam"
 *  heading that is neither of them. `people` carries them separately, which is what it
 *  is for. Same fan-out the workload figures use, and for the same reason.
 */
function headings(t: Task, by: GroupBy): string[] {
  if (by === 'company') return [t.company || 'Unassigned']
  const doers = (t.people ?? []).filter((p) => p.kind === 'RESPONSIBLE')
  if (doers.length) return doers.map((p) => p.name || p.user_id)
  // The singular field still carries a lone doer on rows filed before `people` existed.
  return [t.responsible_person || 'Nobody yet']
}

function group(tasks: Task[], by: GroupBy = 'company') {
  const m = new Map<string, Task[]>()
  for (const t of tasks) {
    for (const k of headings(t, by)) {
      if (!m.has(k)) m.set(k, [])
      m.get(k)!.push(t)
    }
  }
  return [...m.entries()]
    .map(([company, rows]) => ({
      company,
      rows: rows.sort((a, b) => a.ref - b.ref),
      counts: countsOf(rows),
    }))
    .sort((a, b) => a.company.localeCompare(b.company))
}

/** Days, newest first, each holding the company groups for that day.
 *
 *  Dates are 'YYYY-MM-DD', so a string compare sorts them correctly and a dateless
 *  row (empty string) falls to the bottom rather than to the top — where it would be
 *  the first thing you read. */
function byDate(tasks: Task[], by: GroupBy = 'company') {
  const m = new Map<string, Task[]>()
  for (const t of tasks) {
    const k = t.date || ''
    if (!m.has(k)) m.set(k, [])
    m.get(k)!.push(t)
  }
  return [...m.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, rows]) => ({
      date,
      groups: group(rows, by),
      total: rows.length,
      counts: countsOf(rows),
    }))
}

/** Days, each holding company (or person) groups. One shape whether or not dates
 *  are a heading: without date grouping there is a single unnamed day, so every
 *  render below has one code path rather than two that can drift apart.
 *
 *  Exported because `TaskCards` shows the same rows on a phone. Sharing the function
 *  rather than the intent is what stops the two views grouping, counting or sorting
 *  differently - which they would within a month if each did its own.
 */
export function groupRows(tasks: Task[], { groupByDate = false,
                                           groupBy = 'company' as GroupBy } = {}) {
  return groupByDate
    ? byDate(tasks, groupBy)
    : [{ date: '', groups: group(tasks, groupBy), total: tasks.length,
         counts: countsOf(tasks) }]
}

/** The shape both views render. Named so `TaskCards` can hold it without
 *  re-deriving it from `groupRows`'s return type. */
export type TaskGroups = ReturnType<typeof groupRows>

export function TaskTable({ tasks, onOpen, footer, groupByDate = false,
                            groupBy = 'company', selection,
                            startFolded = false }: {
  tasks: Task[]; onOpen: (t: Task) => void; footer?: string
  groupByDate?: boolean
  groupBy?: GroupBy
  /** Open with the company (or person) groups folded - for the lists that span
   *  weeks. See `Collapse`. */
  startFolded?: boolean
  /** Ticking rows to act on several at once. Absent on every screen that has no
   *  bulk action, and then no column is rendered at all - so nothing else changes
   *  shape just because this exists. */
  selection?: Selection
}) {
  const days = groupRows(tasks, { groupByDate, groupBy })
  // Every company (or person) heading on the screen, so "Collapse all" can shut the
  // lot and know when they are already shut.
  const collapsed = useCollapsed(startFolded)
  const allKeys = days.flatMap(
    (d) => d.groups.map((g) => groupKey(d.date, g.company)))
  let sr = 0
  const picking = !!selection
  // The group headers span the whole width, so the count has to follow the column
  // that comes and goes. Hard-coding 7 left a gap the width of the checkbox in every
  // company heading the moment there were eight.
  const cols = picking ? 8 : 7
  return (
    <div className="card">
      {allKeys.length > 1 && (
        <div className="flex justify-end px-2.5 py-1.5 border-b border-rule">
          <CollapseAll collapsed={collapsed} keys={allKeys} />
        </div>
      )}
      <div className="table-wrap">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {selection && (
                <th className="th w-8">
                  <TickAll selection={selection} tasks={tasks} />
                </th>
              )}
              <th className="th w-10">Sr</th>
              <th className="th w-14">Ref</th>
              <th className="th">Task</th>
              <th className="th w-36">Responsible</th>
              <th className="th w-32">Approver</th>
              <th className="th w-40">Status</th>
              <th className="th w-56">Latest remark</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d) => {
              // Only a heading can be folded, so a day with no date heading is never
              // shut - otherwise "All tasks" on Today would hide every row with no
              // way to get them back.
              const dayKey = groupKey(d.date, '')
              const dayShut = groupByDate && collapsed.has(dayKey)
              // A folded day still spends its serial numbers, for the same reason a
              // folded company does: what a row is numbered must not depend on what
              // else happens to be open.
              if (dayShut) sr += d.groups.reduce((n, g) => n + g.rows.length, 0)
              return (
              <Fragment key={d.date || 'all'}>
                {groupByDate && (
                  <tr>
                    {/* Darker and heavier than the company row below it, so the two
                        levels are told apart at a glance rather than read.

                        A whole day folds away too: on "All time" the headings alone
                        are a long list, and a day you are not looking at should cost
                        one line. The padding lives on the button, so the tap target
                        is the full width of the row rather than the words. */}
                    <td colSpan={cols}
                        className="bg-primary/[0.07] border-y border-rule p-0">
                      <button type="button" onClick={() => collapsed.toggle(dayKey)}
                              aria-expanded={!dayShut}
                              className="w-full text-left px-2.5 py-2 flex items-center
                                         gap-1.5 hover:bg-primary/[0.04]">
                        <Caret open={!dayShut} />
                        <span className="text-[13px] font-semibold tracking-tight">
                          {d.date ? fmtDate(d.date) : 'No date'}
                        </span>
                        <MiniBar c={d.counts} />
                        <span className="mono text-[11.5px] text-ink-3 ml-2">
                          {d.total} task{d.total === 1 ? '' : 's'}
                        </span>
                      </button>
                    </td>
                  </tr>
                )}
                {!dayShut && d.groups.map((g) => (
                <Fragment key={g.company}>
                  <tr>
                    {/* `bg-paper`, not the literal #F7F5F1 it was. That value is the
                        DEFAULT theme's paper, so under either dark theme this group
                        header was a light slab with theme-driven near-white ink on it
                        - the company name was the least readable thing on the screen.
                        Same literal-hex bug as `.th` and the navigation rail. */}
                    <td colSpan={cols} className="bg-paper border-y border-rule p-0">
                      <button type="button"
                              onClick={() => collapsed.toggle(groupKey(d.date, g.company))}
                              aria-expanded={!collapsed.has(groupKey(d.date, g.company))}
                              className="w-full text-left px-2.5 py-1.5 flex items-center
                                         gap-1.5 hover:bg-rule/25">
                        <Caret open={!collapsed.has(groupKey(d.date, g.company))} />
                        <span className="text-[12.5px] font-semibold tracking-tight">{g.company}</span>
                        <MiniBar c={g.counts} />
                        <span className="mono text-[11.5px] text-ink-3 ml-2">
                          {g.rows.length} task{g.rows.length === 1 ? '' : 's'}
                        </span>
                      </button>
                    </td>
                  </tr>
                  {g.rows.map((t, i) => {
                    // Counted even when hidden, so folding one group does not
                    // renumber the rows of another - the serial is a place in the
                    // list, and the list has not changed.
                    sr += 1
                    if (collapsed.has(groupKey(d.date, g.company))) return null
                    // Defensive only. Every task has an id now that the store is a
                    // database - the id IS the primary key - so this is never false.
                    // It used to mean "not yet adopted by the reconciler", a state that
                    // can no longer exist.
                    const openable = !!t.task_id
                    return (
                      <tr key={t.task_id || `pending-${g.company}-${i}`}
                          onClick={openable ? () => onOpen(t) : undefined}
                          className={openable ? 'hover:bg-paper cursor-pointer' : 'bg-pend-tint/30'}>
                        {selection && (
                          // `stopPropagation` on the CELL, not only the input: the
                          // whole row opens the drawer, so a click anywhere in this
                          // column - including the padding beside the box - would
                          // otherwise open a task somebody was trying to tick.
                          <td className="td text-center"
                              onClick={(e) => e.stopPropagation()}>
                            <Tick selection={selection} task={t} />
                          </td>
                        )}
                        <td className="td mono text-xs text-ink-3 text-right relative pl-3">
                          <PriorityEdge priority={t.priority} />
                          {sr}
                        </td>
                        <td className="td mono text-[12.5px] text-primary font-medium">
                          {openable ? `#${t.ref}` : <span className="text-ink-3">—</span>}
                        </td>
                        {/* The task text is the column worth the room. Under
                            table-layout:auto it otherwise loses to the nowrap columns
                            beside it and wraps to four lines on a phone. */}
                        <td className="td min-w-[210px] lg:min-w-0">
                          {t.task}
                          {/* The client, but only when the heading is not already
                              saying it. Grouped by person the company vanished from
                              the row entirely - "finalise the GST working" with no
                              answer to "for whom", which is the first thing anybody
                              asks about a task at an accountancy firm.

                              Below the task text and recessive, so it informs the row
                              without competing with it. There is no Company column to
                              fall back on: the table has never had one, because the
                              company was always the heading. */}
                          {groupBy === 'person' && t.company && (
                            <span className="block text-[11.5px] text-ink-3 mt-0.5">
                              {t.company}
                            </span>
                          )}
                          <span className="flex gap-1.5 mt-1">
                            {t.status === 'REWORK' && <Tag tone="bad">rejected</Tag>}
                            {t.carry_forward_count > 0 && (
                              <Tag>carried {t.carry_forward_count}×</Tag>
                            )}
                            {/* Otherwise a task inside its window reads as an
                                ordinary one that somehow escaped carry-forward -
                                which is the question this answers. */}
                            {t.end_date && <Tag>until {fmtDay(t.end_date)}</Tag>}
                            {t.is_recurring && <Tag>⟳ recurring</Tag>}
                          </span>
                        </td>
                        {/* Wrapping, not nowrap. Both of these hold a JOINED list -
                            "Ravi Deshmukh, Farhan Qureshi, Priya Nair" - so on a task
                            with three people a nowrap cell blew straight past the
                            `w-36` it was given and dragged the whole table sideways.
                            The columns already have widths; letting the names wrap is
                            what makes them mean anything. */}
                        <td className="td text-[12.5px] break-words">
                          {t.responsible_person || <span className="text-ink-3">—</span>}
                        </td>
                        <td className="td text-[12.5px] break-words">
                          {t.approver || <span className="text-ink-3">—</span>}
                          {/* Only when the count is the fact that matters: several
                              approvers, and the task still waiting on them. */}
                          {approvalProgress(t) && (
                            <span className="block text-[11px] text-ink-3">
                              {approvalProgress(t)}
                            </span>
                          )}
                        </td>
                        <td className="td">
                          <StatusChip status={t.status} approvalStatus={t.approval_status} />
                        </td>
                        <td className="td text-xs text-ink-2 max-w-[15rem]">
                          <span className="line-clamp-2">{t.remarks}</span>
                        </td>
                      </tr>
                    )
                  })}
                </Fragment>
                ))}
              </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      {footer && (
        <div className="px-4 py-2.5 border-t border-rule text-xs text-ink-3">{footer}</div>
      )}
    </div>
  )
}
